import { Add, Delete } from '@mui/icons-material';
import ExpandIcon from '@mui/icons-material/Expand';
import FunctionsIcon from '@mui/icons-material/Functions';
import SpaceBarIcon from '@mui/icons-material/SpaceBar';
import TitleIcon from '@mui/icons-material/Title';
import {
  Fab,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as TitleSum } from '@work4all/assets/icons/title_sum.svg';

import { lazyNamed, loadable, Loading } from '@work4all/components';
import { ArticlePicker } from '@work4all/components/lib/components/entity-picker/article-picker/ArticlePicker';
import { EntityPickerPopover } from '@work4all/components/lib/components/entity-picker/components';
import { Tooltip } from '@work4all/components/lib/components/tooltip/Tooltip';
import { Divider } from '@work4all/components/lib/dataDisplay/divider/Divider';
import { NavigationOverlay } from '@work4all/components/lib/navigation/navigation-overlay';

import { usePopoverState } from '@work4all/data/lib/hooks/usePopoverState';

import { Article } from '@work4all/models/lib/Classes/Article.entity';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { ErpPositionsKind } from '@work4all/models/lib/Enums/ErpPositionsKind.enum';

import { useFormContextPlus } from '../../../form-plus/use-form-context-plus';
import { useMaskTabContext } from '../../../mask-tabs';
import { MaskOverlayFullscreenToggleButton } from '../../components/MaskOverlayFullscreenToggleButton';
import { MaskOverlayMenuWrapper } from '../../components/MaskOverlayMenuWrapper';
import { MaskOverlaySubmitButton } from '../../components/MaskOverlaySubmitButton';
import { useMaskContext } from '../../hooks/mask-context';
import { OverlayControllerFavoriteItemAction } from '../../hooks/use-overlay-controller-more-actions/components/OverlayControllerFavoriteItemAction';
import { useStandardDeleteEntityHandler } from '../../hooks/use-standard-delete-entity-handler';
import { ERPSettings } from '../settings/erp-settings/ERPSettings';

import { QuickSaveButton } from './components/quick-save/QuickSaveButton';
import { ReportButton } from './components/tab-panels/report-preview/ReportButton';
import { ErpData } from './ErpData';
import { useShadowBzObjectApiContext } from './hooks/use-bz-shadow-object-api/use-shadow-bz-object-api-context';

const Signature = loadable(
  lazyNamed(() => import('./components/signature/Signature'), 'Signature'),
  <Loading />
);

interface ERPMaskHeaderActionsProps {
  isSubmitting: boolean;
  isDirty: boolean;
  canSave: boolean;
  canDelete: boolean;
  hasError: boolean;
  onQuickSave: () => Promise<void>;
}
export const ERPMaskHeaderActions = (props: ERPMaskHeaderActionsProps) => {
  const { isDirty, isSubmitting, canSave, canDelete, hasError, onQuickSave } =
    props;
  const { t } = useTranslation();
  const mask = useMaskContext();
  const { wip, entity } = mask;

  const disabled = Boolean(
    !isDirty || isSubmitting || !canSave || wip || hasError
  );

  const signatureDisabled = isSubmitting || !canSave || wip || hasError;
  const reportDisabled = signatureDisabled && isDirty;

  const shadowBzObjectApi = useShadowBzObjectApiContext();
  const onAddPosition = shadowBzObjectApi.addPosition;

  const [settingsOpen, setSettingsOpen] = useState(false);

  const openSettings = useCallback(() => {
    setSettingsOpen(true);
  }, []);

  const onSettingsClose = useCallback(() => {
    setSettingsOpen(false);
  }, []);

  const handleDeleteEntitiesClick = useStandardDeleteEntityHandler(
    mask,
    undefined,
    t(`COMMON.ERP.DELETE.${entity.toUpperCase()}`)
  );

  const maskTabContext = useMaskTabContext();

  const { ...articlePopoverProps } = usePopoverState();

  const isMobile = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('md')
  );

  const erpSettingsView = useMemo(() => {
    return {
      view: <ERPSettings amplitudeEntryPoint="ERPMask" />,
    };
  }, []);

  const { watch } = useFormContextPlus<ErpData>();

  const favoriteItem = watch('favoriteItem');

  return (
    <React.Fragment>
      <NavigationOverlay
        open={settingsOpen}
        initialView={erpSettingsView}
        close={onSettingsClose}
      />
      {entity === Entities.deliveryNote ? (
        <Signature disabled={signatureDisabled} />
      ) : null}

      <ReportButton disabled={reportDisabled} />

      {entity !== Entities.inboundInvoice && (
        <QuickSaveButton
          isDirty={isDirty}
          isSubmitting={isSubmitting}
          isDisabled={!canSave || wip || hasError}
          onQuickSave={onQuickSave}
        />
      )}

      {isMobile && maskTabContext.value === 'positions' && (
        <EntityPickerPopover
          picker={
            <ArticlePicker
              multiple={false}
              onChange={(value) => {
                if (value) {
                  if ((value as ErpPositionsKind) in ErpPositionsKind) {
                    onAddPosition({ positionType: value as ErpPositionsKind });
                  } else {
                    onAddPosition({
                      positionType: ErpPositionsKind.STANDARD,
                      article: value as Article,
                    });
                  }
                }
                articlePopoverProps.onClose();
              }}
              value={null}
            />
          }
          fullscreen
        >
          <Fab size="medium" color="primary">
            <Add />
          </Fab>
        </EntityPickerPopover>
      )}

      <MaskOverlaySubmitButton disabled={disabled} />

      <MaskOverlayFullscreenToggleButton />

      <MaskOverlayMenuWrapper>
        <MenuList>
          {!(isMobile && maskTabContext.value === 'positions') && (
            <>
              <MenuItem onClick={openSettings}>{t('MASK.SETTINGS')}</MenuItem>
              <Tooltip
                title={
                  canDelete || mask.isCreateMode
                    ? undefined
                    : t('RIGHTS.MISSING')
                }
                activateForDisabled={true}
              >
                <MenuItem
                  disabled={!canDelete}
                  onClick={handleDeleteEntitiesClick}
                >
                  <Delete />
                  <Typography marginLeft="0.5rem">
                    {t('COMMON.DELETE')}
                  </Typography>
                </MenuItem>
              </Tooltip>
              {mask.isEditMode && (
                <OverlayControllerFavoriteItemAction
                  id={mask.id}
                  entity={entity}
                  isFavorite={Boolean(favoriteItem)}
                />
              )}
            </>
          )}
          {isMobile && maskTabContext.value === 'positions' && (
            <>
              <MenuItem
                onClick={() =>
                  onAddPosition({
                    positionType: ErpPositionsKind.TEXTZEILE,
                  })
                }
              >
                <ListItemIcon>
                  <SpaceBarIcon />
                </ListItemIcon>
                <ListItemText>{t('COMMON.ERP.EMPTY')}</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() =>
                  onAddPosition({
                    positionType: ErpPositionsKind.SEITENUMBRUCH,
                  })
                }
              >
                <ListItemIcon>
                  <ExpandIcon />
                </ListItemIcon>
                <ListItemText>{t('COMMON.PAGE_BREAK')}</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() =>
                  onAddPosition({
                    positionType: ErpPositionsKind.TITEL,
                  })
                }
              >
                <ListItemIcon>
                  <TitleIcon />
                </ListItemIcon>
                <ListItemText> {t('COMMON.TITLE')}</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() =>
                  onAddPosition({
                    positionType: ErpPositionsKind.TITELSUMME,
                  })
                }
              >
                <ListItemIcon>
                  <TitleSum />
                </ListItemIcon>
                <ListItemText> {t('COMMON.ERP.TITLE_TOTAL')}</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() =>
                  onAddPosition({
                    positionType: ErpPositionsKind.ZWISCHENSUMME,
                  })
                }
              >
                <ListItemIcon>
                  <FunctionsIcon />
                </ListItemIcon>
                <ListItemText> {t('COMMON.ERP.SUB_TOTAL')}</ListItemText>
              </MenuItem>
              <Divider />
              <MenuItem disabled>{t('COMMON.PREVIEW')}</MenuItem>
              {mask.isEditMode && (
                <OverlayControllerFavoriteItemAction
                  id={mask.id}
                  entity={entity}
                  isFavorite={Boolean(favoriteItem)}
                />
              )}
            </>
          )}
        </MenuList>
      </MaskOverlayMenuWrapper>
    </React.Fragment>
  );
};
